import React from 'react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const majorChallenges = [
    {
      number: '1',
      title: 'Fragmented Data & Lack of Interoperability',
      description: 'Patient health records are often isolated across different systems and providers, preventing a comprehensive view of a patient\'s medical history.',
      consequence: 'Incomplete patient information can lead to misdiagnoses, delayed treatment, and redundant tests, increasing costs and patient frustration.',
      impact: 'Patients experience fragmented care, providers struggle with inefficiencies, and healthcare systems bear higher operational costs.',
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      number: '2',
      title: 'High Administrative Burden',
      description: 'Healthcare professionals spend significant time on paperwork, billing, and regulatory compliance rather than direct patient care.',
      consequence: 'Studies show physicians spend nearly two hours on administrative tasks for every one hour with patients. This contributes to burnout.',
      impact: 'Providers face increased stress and reduced capacity for patient care, while healthcare organizations incur substantial overheads.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      number: '3',
      title: 'Inefficient Access to Quality Care',
      description: 'Barriers such as geographic location, socioeconomic status, and specialist shortages limit timely access to essential healthcare services.',
      consequence: 'Rural communities often lack access to specialized medical expertise, forcing patients to travel long distances or delay critical care.',
      impact: 'Patients in underserved areas experience poorer health outcomes and greater health disparities.',
      gradient: 'from-green-500 to-teal-500',
    },
  ];

  const specificChallenges = [
    { icon: '🔒', title: 'Data Silos', description: 'Patient data trapped in incompatible systems, hindering care coordination and creating costly duplicate tests.' },
    { icon: '📠', title: 'Slow Medical Record Retrieval', description: 'Outdated methods like faxing and mailing medical records cause patient delays and increased expenses.' },
    { icon: '⚖️', title: 'FHIR Compliance Challenges', description: 'New compliance is already in effect and must be met by December 2025.' },
    { icon: '💰', title: 'High Administrative Costs', description: 'Manual processes drive up costs including labor, supplies, and legal fees.' },
    { icon: '⏱️', title: 'Delayed Claims Processing', description: 'Slow record transfers delay claims processing and impact revenue cycle.' },
    { icon: '🛡️', title: 'Reliability and Data Security', description: 'Security and reliability concerns for sending and receiving patient data.' },
  ];

  return (
    <div className="min-h-screen">
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full mb-6">
                <span className="w-2 h-2 bg-blue-600 rounded-full mr-2 animate-pulse" />
                <span className="text-blue-700 font-semibold text-sm">Healthcare Data Revolution</span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight" data-testid="hero-title">
                Health Records{' '}<span className="bg-gradient-to-r from-blue-600 to-cyan-500 text-transparent bg-clip-text">At Your Fingertips</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-700 mb-6 leading-relaxed">The future of global health is connecting patients, records, and providers worldwide at the touch of a button</p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">Streamline operations, enhance patient care, and reduce administrative burdens with instant access to interoperable patient records through our robust technology platform paired with a patient management app.</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/solutions" className="px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 text-center" data-testid="cta-get-started">Get Started</Link>
                <a href="#contact" className="px-8 py-4 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold text-lg hover:border-blue-500 hover:text-blue-600 hover:shadow-lg transition-all duration-300 text-center" data-testid="cta-contact">Contact Us</a>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl bg-white p-8">
                <img src="https://customer-assets.emergentagent.com/job_healthcare-system-5/artifacts/hstbld5s_%7BBackground%7DHealthcare%20at%20your%20Fingertips_caretapai.png" alt="Dr. Barry Blue" className="w-full h-auto rounded-2xl" />
              </div>
              <div className="absolute -bottom-6 -right-6 w-full h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What We Do</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">We Help Overcome Major Hurdles Within Health Care Such As:</p>
          </div>
          <div className="space-y-8">
            {majorChallenges.map((challenge, index) => (
              <div key={index} className="bg-gradient-to-r from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
                <div className="p-8 md:p-12">
                  <div className="flex items-start gap-6">
                    <div className={`flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br ${challenge.gradient} flex items-center justify-center text-white text-3xl font-bold shadow-lg`}>{challenge.number}</div>
                    <div className="flex-1">
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{challenge.title}</h3>
                      <p className="text-lg text-gray-700 mb-4 leading-relaxed">{challenge.description}</p>
                      <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4 rounded-r-lg"><p className="text-orange-900"><strong>Consequence:</strong> {challenge.consequence}</p></div>
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg"><p className="text-blue-900"><strong>Impact:</strong> {challenge.impact}</p></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specificChallenges.map((challenge, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100">
                <div className="text-4xl mb-3">{challenge.icon}</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{challenge.title}</h4>
                <p className="text-gray-600 leading-relaxed">{challenge.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Solutions</h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">Comprehensive technology solutions designed to transform healthcare delivery</p>
          <Link to="/solutions" className="inline-block px-10 py-5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300">Explore Our Solutions</Link>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Who We Serve</h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">Empowering healthcare providers, insurers, legal firms, and government agencies</p>
          <Link to="/who-we-serve" className="inline-block px-10 py-5 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300">Learn More</Link>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">Truly Personalized Health Care Backed By State of The Art Technology</h3>
          <p className="text-xl text-cyan-100">Our robust cloud-based SaaS platform links to a patient engagement app for a complete health ecosystem</p>
        </div>
      </section>

      <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-xl text-gray-600">We're here to help you revolutionize healthcare with advanced technology</p>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mb-4">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">CareTap Nexus</h3>
              <div className="space-y-4 text-lg">
                <div className="flex items-center justify-center gap-3">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  <a href="tel:6154778401" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">(615) 477-8401</a>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  <a href="mailto:info@caretapnexus.com" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">info@caretapnexus.com</a>
                </div>
              </div>
              <a href="mailto:info@caretapnexus.com" className="mt-6 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300">Email Us</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;